<script>
/* COMPONENTS */
import GaContentView from '@components/v2/layout/GaContentView.vue';
import GaListView from '@components/v2/layout/GaListView.vue';
import AkInputFilter from '@components/v2/filter/AkInputFilter';
import AkListFilter from '@components/v2/filter/AkListFilter';
import AkAllFilter from '@components/v2/filter/AkAllFilter.vue';

/* MIXINS */
import messageMixin from '@mixins/messageMixin';
import utilsMixin from '@mixins/utilsMixin';
import companyMixin from '@mixins/companyMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import filterListMixin from '@mixins/filterListMixin';
import sidebarMixin from '@mixins/sidebarMixin';
import ownerTypesConst from '@mixins/const/ownerTypesConst';

/* SERVICES */
import ownerService from '@services/ownerService';
import CrmCard from '@views/crm/components/CrmCard.vue';
import loaderMixin from '@mixins/loaderMixin';

export default {
  components: {
    CrmCard,
    GaContentView,
    GaListView,
    AkInputFilter,
    AkListFilter,
    AkAllFilter,
  },
  mixins: [
    permissionsMixin,
    messageMixin,
    utilsMixin,
    loaderMixin,
    companyMixin,
    filterListMixin,
    sidebarMixin,
    ownerTypesConst,
  ],
  data() {
    return {
      commercialId: undefined,
      crmList: [],
      filter: {},
    };
  },
  mounted() {
    this.commercialId = this.$route.params.commercialId;
    this.search();
  },
  computed: {
    getNewOwner() {
      return this.crmList?.newOwner ?? [];
    },
    getEnCoursOwner() {
      return this.crmList?.enCoursOwner ?? [];
    },
    getTermineOwner() {
      return this.crmList?.termineOwner ?? [];
    },
  },
  methods: {
    exportCrm() {
      this.showTotalLoaderWithAfter(this.$t('export_in_progress'));
      ownerService.exporOwnerCrm(this.filter).then((data) => {
        this.hideLoader();
      });
    },
    resetFilter() {
      this.filter = {};
      this.toggleFilterTitle('GaCrmFilterList', this.$t('filter_all'), this.filter, this.clickSearch, this.resetFilter);
    },
    search() {
      this.filter.commercialId = this.commercialId;
      this.$nextTick(() => {
        this.hideLoader();
        this.showTotalLoaderWithAfter('Récupération des clients en cours...');
      });
      ownerService
        .ownerCrm(this.filter)
        .then((data) => {
          this.list = data;
        })
        .catch((e) => {
          this.addError(this.$t('error.' + e.response.data.error));
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(() => {
            this.hideLoader();
          });
        });
    },
    openFilterPanel() {
      this.toggleFilterTitle('GaCrmFilterList', this.$t('filter_all'), this.filter, this.clickSearch, this.resetFilter);
    },
  },
};
</script>

<template>
  <GaContentView>
    <template #content>
      <span class="title-border secondary">CRM</span>
      <GaListView>
        <template #action>
          <div class="d-flex justify-content-end" style="gap: 16px">
            <button class="btn-grey xs" @click="exportCrm()">
              {{ this.$t('payment.download') }}
            </button>
          </div>
        </template>
        <template #filter>
          <AkListFilter
            v-if="this.isGas()"
            :multi-selection="true"
            v-model="this.filter.companyIds"
            :items="this.companies"
            :display-filter="true"
            @search="this.search"
            :label="$t('employee.company')"
            direction="right" />
          <AkInputFilter @search="search" v-model="filter.ownerName" :label="$t('interlocutor.prospect')" />
          <AkInputFilter @search="search" v-model="filter.email" :label="$t('contact.email')" />
          <AkAllFilter @open="this.openFilterPanel" />
        </template>
        <template #content>
          <div class="flex md:flex-row flex-col w-full gap-3">
            <div class="flex-1 bg-grey-50 bg-opacity-50 rounded-lg p-3 h-min" v-if="!this.isOwner()">
              <div class="flex justify-between items-center px-2 py-4">
                <div class="flex gap-2 items-center">
                  <i
                    class="ga-icon ga-chevron_up text-2xl leading-[16px] cursor-pointer text-grey-400 md:hidden"
                    @click="this.toggleElement(this.$refs, 'newList', $event)" />
                  <span class="text-grey-900 font-semibold text-base uppercase">Nouveau</span>
                </div>
                <span class="text-grey-900 font-semibold text-base uppercase">{{ getNewOwner.length }}</span>
              </div>
              <div ref="newList">
                <perfect-scrollbar
                  class="flex flex-col gap-2 mt-3 md:max-h-[calc(100vh_-_428px)] h-full"
                  v-if="this.getNewOwner.length > 0">
                  <CrmCard :owner="item" v-for="item of this.getNewOwner" :key="item.dateCreated" />
                </perfect-scrollbar>
              </div>
            </div>
            <div class="flex-1 bg-grey-50 bg-opacity-50 rounded-lg p-3 h-min">
              <div class="flex justify-between items-center px-2 py-4">
                <div class="flex gap-2 items-center">
                  <i
                    class="ga-icon ga-chevron_up text-2xl leading-[16px] cursor-pointer text-grey-400 md:hidden"
                    @click="this.toggleElement(this.$refs, 'progressList', $event)" />
                  <span class="text-grey-900 font-semibold text-base uppercase">En cours</span>
                </div>
                <span class="text-grey-900 font-semibold text-base uppercase">{{ getEnCoursOwner.length }}</span>
              </div>
              <div ref="progressList">
                <perfect-scrollbar
                  class="flex flex-col gap-2 mt-3 md:max-h-[calc(100vh_-_428px)] h-full"
                  v-if="this.getEnCoursOwner.length > 0">
                  <CrmCard :owner="item" v-for="item of this.getEnCoursOwner" :key="item.dateCreated" />
                </perfect-scrollbar>
              </div>
            </div>
            <div class="flex-1 bg-grey-50 bg-opacity-50 rounded-lg p-3 h-min">
              <div class="flex justify-between items-center px-2 py-4">
                <div class="flex gap-2 items-center">
                  <i
                    class="ga-icon ga-chevron_up text-2xl leading-[16px] cursor-pointer text-grey-400 md:hidden"
                    @click="this.toggleElement(this.$refs, 'closedList', $event)" />
                  <span class="text-grey-900 font-semibold text-base uppercase">Terminé</span>
                </div>
                <span class="text-grey-900 font-semibold text-base uppercase">{{ getTermineOwner.length }}</span>
              </div>
              <div ref="closedList">
                <perfect-scrollbar
                  class="flex flex-col gap-2 mt-3 md:max-h-[calc(100vh_-_428px)] h-full"
                  v-if="this.getTermineOwner.length > 0">
                  <CrmCard :owner="item" v-for="item of this.getTermineOwner" :key="item.dateCreated" />
                </perfect-scrollbar>
              </div>
            </div>
          </div>
        </template>
      </GaListView>
    </template>
  </GaContentView>
</template>
